import React, { useState, useEffect } from 'react';
import './GetQuote.css';

import { FaUser, FaEnvelope, FaPhone, FaMapMarkerAlt, FaClock, FaComment, FaAngleDown } from 'react-icons/fa';

import axios from "axios";
import { FaCalendarDays, FaShirt } from 'react-icons/fa6';
import { Heading } from '../../../components';

// API endpoint for form submission
const API_PATH = 'https://sparkle-react.asdesignsgalaxy.com/api/phpmailer/quote.php';

// Regular expressions for email and phone number validation
const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
const PHONE_REGEX = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;

const GetQuote = () => {

  // State for form data with initial empty values
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    number: '',
    serviceType: '',
    address: '',
    pickupDate: '',
    pickupTime: '',
    instructions: '',
    extraServices: []
  });

  // State for tracking form submission status
  const [mailSent, setMailSent] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Effect to automatically hide success message after 3 seconds
  useEffect(() => {
    if (mailSent) {
      const timer = setTimeout(() => setMailSent(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [mailSent]);

  // Form submission handler
  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Field validation
      const validationErrors = [];

      if (!formData.name.trim()) validationErrors.push('Name is required');
      if (!formData.email.trim()) {
        validationErrors.push('Email is required');
      } else if (!EMAIL_REGEX.test(formData.email)) {
        validationErrors.push('Invalid email format');
      }
      if (!formData.number.trim()) {
        validationErrors.push('Phone number is required');
      } else if (!PHONE_REGEX.test(formData.number)) {
        validationErrors.push('Invalid phone number format');
      }
      if (!formData.serviceType) validationErrors.push('Service type is required');
      if (!formData.address) validationErrors.push('Pickup location is required');
      if (!formData.pickupDate) validationErrors.push('Pickup date is required');
      if (!formData.pickupTime) validationErrors.push('Pickup time is required');
      if (!formData.instructions) validationErrors.push('Additional instructions are required');

      // If validation errors exist, throw them
      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join(' | '));
      }

      // Prepare form data for submission
      const postData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach(item => postData.append(`${key}[]`, item));
        } else {
          postData.append(key, value);
        }
      });

      // Make API call to submit form data
      const response = await axios.post(API_PATH, postData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Handle API response
      if (!response.data.sent) {
        throw new Error(response.data.message || 'Form submission failed');
      }

      // On success, reset form and show success message
      setMailSent(true);
      setFormData({
        name: '',
        email: '',
        number: '',
        serviceType: '',
        address: '',
        pickupDate: '',
        pickupTime: '',
        instructions: '',
        extraServices: []
      });
    } catch (err) {
      // Handle errors from API or validation
      setError(err.response?.data?.message || err.message || 'An error occurred');
    } finally {
      // Reset submission state
      setIsSubmitting(false);
    }
  };

  // Handler for text input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handler for checkbox changes (extra services)
  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;
    setFormData(prev => {
      if (checked) {
        return { ...prev, extraServices: [...prev.extraServices, value] };
      } else {
        return { ...prev, extraServices: prev.extraServices.filter(item => item !== value) };
      }
    });
  };

  return (
    <section className="request-quote custom-form">
      <div className="box-container">
        <div className="content">
          <form className="dark-form" onSubmit={handleSubmitForm}>

            {/* Form Heading */}
            <Heading title={"Request Quote"} subTitle={"schedule your service"} sideHeading linear />

            <div className="input-box">

              {/* Name Input Field */}
              <div className="input-field">
                <label htmlFor="name"><FaUser /></label>
                <input
                  type="text"
                  name="name"
                  className="box"
                  id="name"
                  placeholder="Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Email Input Field */}
              <div className="input-field">
                <label htmlFor="email"><FaEnvelope /></label>
                <input
                  type="email"
                  name="email"
                  className="box"
                  id="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="input-box">

              {/* Phone Number Input Field */}
              <div className="input-field">
                <label htmlFor="number"><FaPhone /></label>
                <input
                  type="tel"
                  name="number"
                  className="box"
                  id="number"
                  placeholder="Phone Number"
                  value={formData.number}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Service Type Select Field */}
              <div className="select">  
                <div className="select-container">
                  <label htmlFor="serviceType"><FaShirt /></label>
                  <select
                    className="select-box"
                    id="serviceType"
                    name="serviceType"
                    value={formData.serviceType}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Service Type</option>
                    <option value="Regular Laundry">Regular Laundry</option>
                    <option value="Dry Cleaning">Dry Cleaning</option>
                    <option value="Stain Removal">Stain Removal</option>
                    <option value="Fabric Alterations">Fabric Alterations</option>
                    <option value="Express Service">Express Service</option>
                    <option value="Eco-Friendly Cleaning">Eco-Friendly Cleaning</option>
                  </select>
                  <div className="icon-container">
                    <FaAngleDown className="icon" />
                  </div>
                </div>
              </div>
            </div>

            <div className="input-box">

              {/* Pickup Location Input Field */}
              <div className="input-field">
                <label htmlFor="address"><FaMapMarkerAlt /></label>
                <input
                  type="text"
                  name="address"
                  className="box"
                  id="address"
                  placeholder="Pickup Location"
                  value={formData.address}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>
            
            <div className="input-box">

              {/* Pickup Date Input Field */}
              <div className="input-field"> 
                <label htmlFor="pickupDate"><FaCalendarDays /></label>
                <input
                  type="text"
                  name="pickupDate"
                  placeholder="Pickup Date"
                  id="pickupDate"
                  className="box"
                  onFocus={(e) => e.target.type = 'date'}
                  onBlur={(e) => e.target.type = 'text'}
                  value={formData.pickupDate}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Pickup Time Input Field */}
              <div className="input-field">   
                <label htmlFor="pickupTime"><FaClock /></label>
                <input
                  type="text"
                  name="pickupTime"
                  placeholder="Pickup Time"
                  id="pickupTime"
                  className="box"
                  onFocus={(e) => e.target.type = 'time'}
                  onBlur={(e) => e.target.type = 'text'}
                  value={formData.pickupTime}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Additional Instructions Text Area */}
            <div className="input-field text-area">
              <label htmlFor="instructions"><FaComment /></label>
              <textarea
                cols="30"
                rows="10"
                name="instructions"
                className="box"
                id="instructions"
                placeholder="Additional Instructions"
                value={formData.instructions}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
              ></textarea>
            </div>

            {/* Extra Services Checkboxes */}
            <div className="extra-services">

              {/* Stain Removal Checkbox */}
              <div className="checkbox-label">
                <input
                  type="checkbox"
                  id="stainRemovalCheckbox"
                  name="extraServices[]"
                  value="Stain Removal"
                  checked={formData.extraServices.includes('Stain Removal')}
                  onChange={handleCheckboxChange}
                  disabled={isSubmitting}
                />
                <label htmlFor="stainRemovalCheckbox">Stain Removal</label>
                <div className="checkbox-indicator"></div>
              </div>

              {/* Express Delivery Checkbox */}
              <div className="checkbox-label">
                <input
                  type="checkbox"
                  id="expressDeliveryCheckbox"
                  name="extraServices[]"
                  value="Express Delivery"
                  checked={formData.extraServices.includes('Express Delivery')}
                  onChange={handleCheckboxChange}
                  disabled={isSubmitting}
                />
                <label htmlFor="expressDeliveryCheckbox">Express Delivery</label>
                <div className="checkbox-indicator"></div>
              </div>

              {/* Alterations Checkbox */}
              <div className="checkbox-label">
                <input
                  type="checkbox"
                  id="alterationsCheckbox"
                  name="extraServices[]"
                  value="Alterations"
                  checked={formData.extraServices.includes('Alterations')}
                  onChange={handleCheckboxChange}
                  disabled={isSubmitting}
                />
                <label htmlFor="alterationsCheckbox">Alterations</label>
                <div className="checkbox-indicator"></div>
              </div>
            </div>

            {/* Submit Button */}
            <button 
              type="submit" 
              className="btn" 
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>

            {/* Error and Success Messages */}
            {error && (
              <span className="alert error" id="msg">
                {error.split(' | ').map((msg, i) => (
                  <span key={i}>{msg}</span>
                ))}
              </span>
            )}
            {mailSent && <span className="alert success" id="msg"><span>Request Submitted Successfully</span></span>}
          </form>
        </div>
      </div>
    </section>
  );
}

export default GetQuote;